/**
 * SchedulerCalendar — month-grid view of scheduled posts across platforms.
 * Pobiera dane z istniejącego /api/scheduler i renderuje kafelki per dzień
 * z kolorami platformowymi. MVP: brak edycji w widoku kalendarza (kliknięcie
 * przeskakuje do listy/edytora w content-scheduler).
 */
import { useMemo, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { apiRequest } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import type {
  ScheduledPost,
  ScheduledPostPlatform,
  ContentPipelineColumn,
  ContentPipelineTask,
} from "@shared/schema";
import { ChevronLeft, ChevronRight, CalendarDays, Download, Workflow } from "lucide-react";

interface SchedulerPostsResponse {
  posts: ScheduledPost[];
}

interface PipelineSnapshotResponse {
  snapshot: { columns: ContentPipelineColumn[]; contentTypes: string[] } | null;
}

const PLATFORM_COLORS: Record<ScheduledPostPlatform, string> = {
  facebook: "bg-blue-500/80",
  instagram: "bg-fuchsia-500/80",
  youtube: "bg-red-500/80",
  tiktok: "bg-pink-500/80",
  linkedin: "bg-sky-600/80",
};

const STATUS_OPACITY: Record<string, string> = {
  scheduled: "opacity-100",
  published: "opacity-50",
  draft: "opacity-40 ring-1 ring-white/30",
  failed: "opacity-90 ring-1 ring-red-500",
};

const WEEKDAYS = ["Pon", "Wt", "Śr", "Czw", "Pt", "Sob", "Nd"];

interface DayCell {
  date: Date;
  inMonth: boolean;
  posts: ScheduledPost[];
}

function buildMonthGrid(viewDate: Date, posts: ScheduledPost[]): DayCell[] {
  const year = viewDate.getFullYear();
  const month = viewDate.getMonth();
  const firstOfMonth = new Date(year, month, 1);
  const dayOfWeekMon0 = (firstOfMonth.getDay() + 6) % 7; // Pon=0
  const gridStart = new Date(year, month, 1 - dayOfWeekMon0);

  const cells: DayCell[] = [];
  for (let i = 0; i < 42; i++) {
    const d = new Date(gridStart);
    d.setDate(gridStart.getDate() + i);
    const dayPosts = posts
      .filter((p) => {
        if (!p.scheduledAt) return false;
        const sd = new Date(p.scheduledAt);
        return (
          sd.getFullYear() === d.getFullYear() &&
          sd.getMonth() === d.getMonth() &&
          sd.getDate() === d.getDate()
        );
      })
      .sort((a, b) => new Date(a.scheduledAt!).getTime() - new Date(b.scheduledAt!).getTime());
    cells.push({ date: d, inMonth: d.getMonth() === month, posts: dayPosts });
  }
  return cells;
}

export default function SchedulerCalendar() {
  const [viewDate, setViewDate] = useState<Date>(() => {
    const now = new Date();
    return new Date(now.getFullYear(), now.getMonth(), 1);
  });
  const [draggingPostId, setDraggingPostId] = useState<string | null>(null);
  const [hoverDayKey, setHoverDayKey] = useState<string | null>(null);
  const qc = useQueryClient();
  const { toast } = useToast();

  const { data, isLoading } = useQuery<SchedulerPostsResponse>({
    queryKey: ["/api/scheduler", "calendar"],
    queryFn: () => apiRequest("GET", "/api/scheduler") as Promise<SchedulerPostsResponse>,
  });

  const { data: pipelineData } = useQuery<PipelineSnapshotResponse>({
    queryKey: ["/api/content-pipeline", "calendar"],
    queryFn: () => apiRequest("GET", "/api/content-pipeline") as Promise<PipelineSnapshotResponse>,
    staleTime: 60_000,
  });
  const pipelineTasksScheduled: ContentPipelineTask[] = useMemo(() => {
    const cols = pipelineData?.snapshot?.columns ?? [];
    return cols.flatMap((c) => c.tasks).filter((t) => t.scheduledAt);
  }, [pipelineData]);

  const rescheduleMutation = useMutation({
    mutationFn: ({ id, scheduledAt }: { id: string; scheduledAt: string }) =>
      apiRequest("PUT", `/api/scheduler/${id}`, { scheduledAt }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["/api/scheduler"] });
      toast({ title: "Przeniesiono", description: "Termin publikacji zaktualizowany" });
    },
    onError: (err: unknown) => {
      const msg = err instanceof Error ? err.message : "Nie udało się zmienić terminu";
      toast({ title: "Błąd", description: msg, variant: "destructive" });
    },
  });

  const [viewMode, setViewMode] = useState<"month" | "week">("month");
  const [platformFilter, setPlatformFilter] = useState<Set<ScheduledPostPlatform>>(() => new Set());
  const allPosts = data?.posts ?? [];
  const posts =
    platformFilter.size > 0 ? allPosts.filter((p) => platformFilter.has(p.platform)) : allPosts;
  const grid = useMemo(() => buildMonthGrid(viewDate, posts), [viewDate, posts]);

  const today = new Date();
  const monthLabel = viewDate.toLocaleDateString("pl-PL", { month: "long", year: "numeric" });

  // Week view: derive 7 days starting Monday of current viewDate's week
  const weekDays = useMemo(() => {
    if (viewMode !== "week") return [];
    const start = new Date(viewDate);
    const dayOfWeekMon0 = (start.getDay() + 6) % 7;
    start.setDate(start.getDate() - dayOfWeekMon0);
    const days: { date: Date; posts: ScheduledPost[] }[] = [];
    for (let i = 0; i < 7; i++) {
      const d = new Date(start);
      d.setDate(start.getDate() + i);
      const dayPosts = posts
        .filter((p) => {
          if (!p.scheduledAt) return false;
          const sd = new Date(p.scheduledAt);
          return (
            sd.getFullYear() === d.getFullYear() &&
            sd.getMonth() === d.getMonth() &&
            sd.getDate() === d.getDate()
          );
        })
        .sort((a, b) => new Date(a.scheduledAt!).getTime() - new Date(b.scheduledAt!).getTime());
      days.push({ date: d, posts: dayPosts });
    }
    return days;
  }, [viewMode, viewDate, posts]);

  const goPrev = () =>
    setViewDate((prev) => {
      if (viewMode === "week") {
        const d = new Date(prev);
        d.setDate(d.getDate() - 7);
        return d;
      }
      return new Date(prev.getFullYear(), prev.getMonth() - 1, 1);
    });
  const goNext = () =>
    setViewDate((prev) => {
      if (viewMode === "week") {
        const d = new Date(prev);
        d.setDate(d.getDate() + 7);
        return d;
      }
      return new Date(prev.getFullYear(), prev.getMonth() + 1, 1);
    });
  const goToday = () =>
    setViewDate(new Date(today.getFullYear(), today.getMonth(), today.getDate()));

  return (
    <div className="relative flex flex-col h-full overflow-hidden">
      <div className="flex items-center justify-between px-4 py-3 border-b border-white/[0.06]">
        <div className="flex items-center gap-2">
          <button
            onClick={() => {
              // Eksport respektuje filtr platformy. Dorzuca pipeline tasks ze scheduledAt.
              const exportPosts = posts; // filtered by platformFilter
              const exportPipelineTasks = pipelineTasksScheduled;
              if (exportPosts.length === 0 && exportPipelineTasks.length === 0) {
                toast({
                  title: "Brak elementów do eksportu",
                  description:
                    platformFilter.size > 0
                      ? "Zmień filtr platformy lub wyczyść go"
                      : "Zaplanuj posty przed eksportem kalendarza",
                });
                return;
              }
              const escape = (s: string) =>
                s
                  .replace(/\\/g, "\\\\")
                  .replace(/;/g, "\\;")
                  .replace(/,/g, "\\,")
                  .replace(/\n/g, "\\n");
              const fmt = (d: Date) =>
                `${d.getUTCFullYear()}${String(d.getUTCMonth() + 1).padStart(2, "0")}${String(d.getUTCDate()).padStart(2, "0")}T${String(d.getUTCHours()).padStart(2, "0")}${String(d.getUTCMinutes()).padStart(2, "0")}00Z`;
              const lines: string[] = [
                "BEGIN:VCALENDAR",
                "VERSION:2.0",
                "PRODID:-//ViralPartners//Scheduler//PL",
                "CALSCALE:GREGORIAN",
                "METHOD:PUBLISH",
              ];
              for (const post of exportPosts) {
                if (!post.scheduledAt) continue;
                const start = new Date(post.scheduledAt);
                const end = new Date(start.getTime() + 30 * 60 * 1000); // 30-min event
                const summary = escape(
                  `[${post.platform}] ${post.title || post.caption?.slice(0, 60) || "Post"}`
                );
                const desc = escape(
                  [
                    post.caption ?? "",
                    `Status: ${post.status}`,
                    post.pageName ? `Strona: ${post.pageName}` : "",
                  ]
                    .filter(Boolean)
                    .join("\n")
                );
                lines.push(
                  "BEGIN:VEVENT",
                  `UID:${post.id}@viral.partners`,
                  `DTSTAMP:${fmt(new Date())}`,
                  `DTSTART:${fmt(start)}`,
                  `DTEND:${fmt(end)}`,
                  `SUMMARY:${summary}`,
                  `DESCRIPTION:${desc}`,
                  `CATEGORIES:${post.platform}`,
                  "END:VEVENT"
                );
              }
              for (const task of exportPipelineTasks) {
                if (!task.scheduledAt) continue;
                const start = new Date(task.scheduledAt);
                const end = new Date(start.getTime() + 30 * 60 * 1000);
                const summary = escape(`[pipeline] ${task.title || "Zadanie"}`);
                const desc = escape(
                  [
                    task.description ?? "",
                    task.process ? `Proces: ${task.process}` : "",
                    task.approvalStatus ? `Akceptacja: ${task.approvalStatus}` : "",
                  ]
                    .filter(Boolean)
                    .join("\n")
                );
                lines.push(
                  "BEGIN:VEVENT",
                  `UID:pipeline-${task.id}@viral.partners`,
                  `DTSTAMP:${fmt(new Date())}`,
                  `DTSTART:${fmt(start)}`,
                  `DTEND:${fmt(end)}`,
                  `SUMMARY:${summary}`,
                  `DESCRIPTION:${desc}`,
                  "CATEGORIES:pipeline",
                  "END:VEVENT"
                );
              }
              lines.push("END:VCALENDAR");
              const blob = new Blob([lines.join("\r\n")], { type: "text/calendar;charset=utf-8" });
              const url = URL.createObjectURL(blob);
              const a = document.createElement("a");
              a.href = url;
              a.download = `viral-partners-schedule-${new Date().toISOString().slice(0, 10)}.ics`;
              a.click();
              URL.revokeObjectURL(url);
              const total = exportPosts.length + exportPipelineTasks.length;
              const detail =
                exportPipelineTasks.length > 0
                  ? `${exportPosts.length} postów + ${exportPipelineTasks.length} pipeline${platformFilter.size > 0 ? ` (filtr: ${platformFilter.size} platform)` : ""}`
                  : `${exportPosts.length} postów${platformFilter.size > 0 ? ` (filtr: ${platformFilter.size} platform)` : ""}`;
              toast({
                title: "Wyeksportowano kalendarz",
                description: `${total} elementów do .ics — ${detail}`,
              });
            }}
            title={
              platformFilter.size > 0
                ? `Eksportuj filtrowany harmonogram (${platformFilter.size} platform) + pipeline tasks do .ics`
                : "Eksportuj harmonogram + pipeline tasks do .ics (Google Calendar / Outlook)"
            }
            className="p-1.5 rounded-md hover:bg-white/[0.06] text-white/60 hover:text-white transition-colors"
          >
            <Download className="w-4 h-4" />
          </button>
          <CalendarDays className="w-4 h-4 text-[#ef3e29]" />
          <h2 className="text-sm font-semibold text-white">Kalendarz publikacji</h2>
          <span className="text-xs text-white/50 capitalize">{monthLabel}</span>
          <div className="ml-2 flex items-center gap-0.5 rounded-md bg-white/[0.04] p-0.5">
            {(["month", "week"] as const).map((mode) => (
              <button
                key={mode}
                onClick={() => setViewMode(mode)}
                className={`px-2 py-0.5 text-[10px] font-medium rounded transition-colors ${
                  viewMode === mode
                    ? "bg-[#ef3e29]/20 text-[#ef3e29]"
                    : "text-white/50 hover:text-white"
                }`}
              >
                {mode === "month" ? "Miesiąc" : "Tydzień"}
              </button>
            ))}
          </div>
        </div>
        <div className="flex items-center gap-1">
          <input
            type="date"
            value={`${viewDate.getFullYear()}-${String(viewDate.getMonth() + 1).padStart(2, "0")}-${String(viewDate.getDate()).padStart(2, "0")}`}
            onChange={(e) => {
              const v = e.target.value;
              if (!v) return;
              const [y, m, d] = v.split("-").map(Number);
              if (y && m && d) setViewDate(new Date(y, m - 1, d));
            }}
            title="Skocz do daty"
            className="px-2 py-1 text-[11px] bg-white/[0.04] border border-white/[0.08] rounded-md text-white/70 outline-none focus:border-white/20 [color-scheme:dark]"
          />
          <button
            onClick={goPrev}
            className="p-1.5 rounded-md hover:bg-white/[0.06] text-white/60 hover:text-white transition-colors"
          >
            <ChevronLeft className="w-4 h-4" />
          </button>
          <button
            onClick={goToday}
            className="px-2.5 py-1 text-[11px] font-medium rounded-md bg-white/[0.04] hover:bg-white/[0.08] text-white/70 hover:text-white transition-colors"
          >
            Dziś
          </button>
          <button
            onClick={goNext}
            className="p-1.5 rounded-md hover:bg-white/[0.06] text-white/60 hover:text-white transition-colors"
          >
            <ChevronRight className="w-4 h-4" />
          </button>
        </div>
      </div>

      <div className="flex items-center gap-2 px-4 py-2 border-b border-white/[0.06] bg-white/[0.02] flex-wrap">
        {(Object.entries(PLATFORM_COLORS) as [ScheduledPostPlatform, string][]).map(
          ([platform, cls]) => {
            const isActive = platformFilter.has(platform);
            const filterEmpty = platformFilter.size === 0;
            return (
              <button
                key={platform}
                onClick={() => {
                  setPlatformFilter((prev) => {
                    const next = new Set(prev);
                    if (next.has(platform)) next.delete(platform);
                    else next.add(platform);
                    return next;
                  });
                }}
                className={`inline-flex items-center gap-1.5 px-1.5 py-0.5 rounded-md border text-[10px] capitalize transition-all ${
                  isActive
                    ? "border-white/[0.2] bg-white/[0.06] text-white"
                    : filterEmpty
                      ? "border-transparent text-white/55 hover:text-white"
                      : "border-transparent text-white/25 hover:text-white/55"
                }`}
              >
                <span className={`w-2 h-2 rounded-full ${cls}`} />
                {platform}
              </button>
            );
          }
        )}
        {platformFilter.size > 0 && (
          <button
            onClick={() => setPlatformFilter(new Set())}
            className="text-[10px] text-white/40 hover:text-white/70 transition-colors"
          >
            Wyczyść filtr
          </button>
        )}
        {pipelineTasksScheduled.length > 0 && (
          <span className="inline-flex items-center gap-1.5 px-1.5 py-0.5 rounded-md text-[10px] text-violet-200 border border-dashed border-violet-500/40">
            <Workflow className="w-2.5 h-2.5" />
            pipeline ({pipelineTasksScheduled.length})
          </span>
        )}
        <span className="text-[10px] text-white/30 ml-auto">
          {platformFilter.size > 0
            ? `Filtr: ${platformFilter.size}/${Object.keys(PLATFORM_COLORS).length} platform · ${posts.length} postów`
            : "Kliknij platformę aby filtrować"}
        </span>
      </div>

      {viewMode === "month" && (
        <div className="grid grid-cols-7 border-b border-white/[0.06] bg-white/[0.02]">
          {WEEKDAYS.map((d) => (
            <div key={d} className="px-2 py-1.5 text-[10px] text-white/40 font-medium text-center">
              {d}
            </div>
          ))}
        </div>
      )}

      {viewMode === "week" ? (
        <div className="flex-1 overflow-auto p-3 space-y-2">
          {weekDays.map((day, i) => {
            const isToday =
              day.date.getFullYear() === today.getFullYear() &&
              day.date.getMonth() === today.getMonth() &&
              day.date.getDate() === today.getDate();
            const dayKey = `${day.date.getFullYear()}-${day.date.getMonth()}-${day.date.getDate()}`;
            const isHover = hoverDayKey === dayKey;
            return (
              <div
                key={i}
                onDragOver={(e) => {
                  if (!draggingPostId) return;
                  e.preventDefault();
                  e.dataTransfer.dropEffect = "move";
                  if (hoverDayKey !== dayKey) setHoverDayKey(dayKey);
                }}
                onDragLeave={() => {
                  if (hoverDayKey === dayKey) setHoverDayKey(null);
                }}
                onDrop={(e) => {
                  e.preventDefault();
                  setHoverDayKey(null);
                  const postId = e.dataTransfer.getData("text/plain") || draggingPostId;
                  setDraggingPostId(null);
                  if (!postId) return;
                  const post = posts.find((p) => p.id === postId);
                  if (!post || !post.scheduledAt) return;
                  const original = new Date(post.scheduledAt);
                  const target = new Date(day.date);
                  target.setHours(original.getHours(), original.getMinutes(), 0, 0);
                  if (target.getTime() === original.getTime()) return;
                  rescheduleMutation.mutate({ id: postId, scheduledAt: target.toISOString() });
                }}
                className={`rounded-lg border transition-colors p-3 ${
                  isHover
                    ? "border-[#ef3e29]/60 bg-[#ef3e29]/[0.10]"
                    : isToday
                      ? "border-[#ef3e29]/40 bg-[#ef3e29]/[0.04]"
                      : "border-white/[0.08] bg-white/[0.02]"
                }`}
              >
                <div className="flex items-center justify-between mb-2 pb-1.5 border-b border-white/[0.04]">
                  <h3
                    className={`text-sm font-semibold capitalize ${isToday ? "text-[#ef3e29]" : "text-white"}`}
                  >
                    {day.date.toLocaleDateString("pl-PL", {
                      weekday: "long",
                      day: "numeric",
                      month: "short",
                    })}
                  </h3>
                  <span className="text-[10px] text-white/40">
                    {day.posts.length} post{day.posts.length === 1 ? "" : "ów"}
                  </span>
                </div>
                {(() => {
                  const dayPipelineTasks = pipelineTasksScheduled.filter((t) => {
                    if (!t.scheduledAt) return false;
                    const td = new Date(t.scheduledAt);
                    return (
                      td.getFullYear() === day.date.getFullYear() &&
                      td.getMonth() === day.date.getMonth() &&
                      td.getDate() === day.date.getDate()
                    );
                  });
                  if (day.posts.length === 0 && dayPipelineTasks.length === 0) {
                    return <p className="text-[11px] text-white/30 italic">Brak zaplanowanych</p>;
                  }
                  return null;
                })()}
                {(day.posts.length > 0 ||
                  pipelineTasksScheduled.some((t) => {
                    if (!t.scheduledAt) return false;
                    const td = new Date(t.scheduledAt);
                    return (
                      td.getFullYear() === day.date.getFullYear() &&
                      td.getMonth() === day.date.getMonth() &&
                      td.getDate() === day.date.getDate()
                    );
                  })) && (
                  <div className="space-y-1">
                    {day.posts.map((post) => {
                      const time = post.scheduledAt
                        ? new Date(post.scheduledAt).toLocaleTimeString("pl-PL", {
                            hour: "2-digit",
                            minute: "2-digit",
                          })
                        : "";
                      const isPublished =
                        post.status === "published" || post.status === "publishing";
                      return (
                        <div
                          key={post.id}
                          draggable={!isPublished}
                          onDragStart={(e) => {
                            if (isPublished) {
                              e.preventDefault();
                              return;
                            }
                            e.dataTransfer.setData("text/plain", post.id);
                            e.dataTransfer.effectAllowed = "move";
                            setDraggingPostId(post.id);
                          }}
                          onDragEnd={() => {
                            setDraggingPostId(null);
                            setHoverDayKey(null);
                          }}
                          title={isPublished ? "" : "Przeciągnij na inny dzień, aby zmienić termin"}
                          className={`flex items-center gap-2 px-2 py-1.5 rounded text-[11px] text-white ${isPublished ? "" : "cursor-grab active:cursor-grabbing"} ${PLATFORM_COLORS[post.platform]} ${STATUS_OPACITY[post.status] ?? ""} ${draggingPostId === post.id ? "opacity-30" : ""}`}
                        >
                          <span className="font-mono text-[10px] w-10 shrink-0">{time}</span>
                          <span className="text-[9px] uppercase opacity-80 w-16 shrink-0">
                            {post.platform}
                          </span>
                          <span className="flex-1 truncate">
                            {post.title || post.caption?.slice(0, 80) || "—"}
                          </span>
                        </div>
                      );
                    })}
                    {pipelineTasksScheduled
                      .filter((t) => {
                        if (!t.scheduledAt) return false;
                        const td = new Date(t.scheduledAt);
                        return (
                          td.getFullYear() === day.date.getFullYear() &&
                          td.getMonth() === day.date.getMonth() &&
                          td.getDate() === day.date.getDate()
                        );
                      })
                      .map((task) => {
                        const time = task.scheduledAt
                          ? new Date(task.scheduledAt).toLocaleTimeString("pl-PL", {
                              hour: "2-digit",
                              minute: "2-digit",
                            })
                          : "";
                        return (
                          <div
                            key={`pl-${task.id}`}
                            title={`Pipeline: ${task.title}\nProces: ${task.process ?? "—"}\nZatwierdzenie: ${task.approvalStatus ?? "brak"}`}
                            className="flex items-center gap-2 px-2 py-1.5 rounded text-[11px] text-violet-200 border border-dashed border-violet-500/40 bg-violet-500/[0.05]"
                          >
                            <span className="font-mono text-[10px] w-10 shrink-0">{time}</span>
                            <Workflow className="w-3 h-3 shrink-0" />
                            <span className="text-[9px] uppercase opacity-80 w-16 shrink-0">
                              pipeline
                            </span>
                            <span className="flex-1 truncate">{task.title || "—"}</span>
                          </div>
                        );
                      })}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      ) : (
        <div className="grid grid-cols-7 grid-rows-6 flex-1 overflow-auto">
          {grid.map((cell, i) => {
            const isToday =
              cell.date.getFullYear() === today.getFullYear() &&
              cell.date.getMonth() === today.getMonth() &&
              cell.date.getDate() === today.getDate();
            const dayKey = `${cell.date.getFullYear()}-${cell.date.getMonth()}-${cell.date.getDate()}`;
            const isHover = hoverDayKey === dayKey;
            return (
              <div
                key={i}
                onDragOver={(e) => {
                  if (!draggingPostId) return;
                  e.preventDefault();
                  e.dataTransfer.dropEffect = "move";
                  if (hoverDayKey !== dayKey) setHoverDayKey(dayKey);
                }}
                onDragLeave={() => {
                  if (hoverDayKey === dayKey) setHoverDayKey(null);
                }}
                onDrop={(e) => {
                  e.preventDefault();
                  setHoverDayKey(null);
                  const postId = e.dataTransfer.getData("text/plain") || draggingPostId;
                  setDraggingPostId(null);
                  if (!postId) return;
                  const post = posts.find((p) => p.id === postId);
                  if (!post || !post.scheduledAt) return;
                  const original = new Date(post.scheduledAt);
                  const target = new Date(cell.date);
                  target.setHours(original.getHours(), original.getMinutes(), 0, 0);
                  if (target.getTime() === original.getTime()) return;
                  rescheduleMutation.mutate({ id: postId, scheduledAt: target.toISOString() });
                }}
                className={`relative border-r border-b border-white/[0.04] p-1.5 min-h-[80px] flex flex-col gap-0.5 transition-colors ${
                  cell.inMonth ? "bg-transparent" : "bg-black/20"
                } ${isHover ? "bg-[#ef3e29]/10 border-[#ef3e29]/40" : ""}`}
              >
                <div
                  className={`text-[10px] mb-0.5 flex items-center justify-between ${
                    cell.inMonth ? "text-white/60" : "text-white/20"
                  }`}
                >
                  <span
                    className={
                      isToday
                        ? "inline-flex items-center justify-center w-5 h-5 rounded-full bg-[#ef3e29] text-white font-bold"
                        : ""
                    }
                  >
                    {cell.date.getDate()}
                  </span>
                  {cell.posts.length > 0 && (
                    <span className="text-[9px] text-white/40">{cell.posts.length}</span>
                  )}
                </div>
                <div className="flex flex-col gap-0.5 overflow-hidden">
                  {cell.posts.slice(0, 4).map((post) => {
                    const time = post.scheduledAt
                      ? new Date(post.scheduledAt).toLocaleTimeString("pl-PL", {
                          hour: "2-digit",
                          minute: "2-digit",
                        })
                      : "";
                    const isPublished = post.status === "published" || post.status === "publishing";
                    const fullCaption = post.caption ?? "";
                    const tooltip = [
                      `[${post.platform.toUpperCase()}] ${time}`,
                      post.title ? `Tytuł: ${post.title}` : null,
                      fullCaption
                        ? `Treść: ${fullCaption.slice(0, 280)}${fullCaption.length > 280 ? "…" : ""}`
                        : null,
                      `Status: ${post.status}`,
                      !isPublished ? "(przeciągnij, aby zmienić termin)" : null,
                    ]
                      .filter(Boolean)
                      .join("\n");
                    return (
                      <div
                        key={post.id}
                        draggable={!isPublished}
                        onDragStart={(e) => {
                          if (isPublished) {
                            e.preventDefault();
                            return;
                          }
                          e.dataTransfer.setData("text/plain", post.id);
                          e.dataTransfer.effectAllowed = "move";
                          setDraggingPostId(post.id);
                        }}
                        onDragEnd={() => {
                          setDraggingPostId(null);
                          setHoverDayKey(null);
                        }}
                        title={tooltip}
                        className={`flex items-center gap-1 px-1 py-0.5 rounded text-[9px] text-white truncate ${isPublished ? "" : "cursor-grab active:cursor-grabbing"} ${PLATFORM_COLORS[post.platform]} ${STATUS_OPACITY[post.status] ?? ""} ${draggingPostId === post.id ? "opacity-30" : ""}`}
                      >
                        <span className="font-mono">{time}</span>
                        <span className="truncate">
                          {post.title || post.caption?.slice(0, 30) || "—"}
                        </span>
                      </div>
                    );
                  })}
                  {cell.posts.length > 4 && (
                    <span className="text-[9px] text-white/40">
                      +{cell.posts.length - 4} więcej
                    </span>
                  )}
                  {(() => {
                    const cellTasks = pipelineTasksScheduled.filter((t) => {
                      if (!t.scheduledAt) return false;
                      const td = new Date(t.scheduledAt);
                      return (
                        td.getFullYear() === cell.date.getFullYear() &&
                        td.getMonth() === cell.date.getMonth() &&
                        td.getDate() === cell.date.getDate()
                      );
                    });
                    return cellTasks.slice(0, 2).map((task) => (
                      <div
                        key={`pl-${task.id}`}
                        title={`Pipeline: ${task.title}\nProces: ${task.process ?? "—"}\nZatwierdzenie: ${task.approvalStatus ?? "brak"}`}
                        className="flex items-center gap-1 px-1 py-0.5 rounded text-[9px] text-violet-200 truncate border border-dashed border-violet-500/40 bg-violet-500/5"
                      >
                        <Workflow className="w-2.5 h-2.5 shrink-0" />
                        <span className="truncate">{task.title || "—"}</span>
                      </div>
                    ));
                  })()}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {isLoading && (
        <div className="px-4 py-2 text-[11px] text-white/40">Ładuję plan publikacji...</div>
      )}
      {!isLoading && posts.length === 0 && (
        <div className="absolute inset-x-0 top-1/2 -translate-y-1/2 pointer-events-none flex items-center justify-center">
          <div className="bg-[#0a0c10]/90 border border-white/[0.08] rounded-xl px-5 py-4 text-center max-w-md backdrop-blur">
            <CalendarDays className="w-8 h-8 mx-auto text-white/30 mb-2" />
            <p className="text-sm text-white/70 font-medium mb-1">Pusty kalendarz</p>
            <p className="text-[11px] text-white/45 leading-relaxed">
              Zaplanowane posty pojawią się tutaj automatycznie. Idź do tabu{" "}
              <span className="text-white/70">Scheduler</span> aby utworzyć pierwszy post.
            </p>
          </div>
        </div>
      )}
    </div>
  );
}
